import { z } from "zod";

/**
 * Onchain Swap Quote API Response Types
 *
 * These schemas define the API contract for swap quote responses.
 * Used by both frontend and backend for type safety.
 */

// Token info in swap quote response
export const swapQuoteTokenInfoSchema = z.object({
  address: z.string(),
  name: z.string(),
  symbol: z.string(),
  decimals: z.number().int(),
  logo: z.string().nullable().optional(),
});

// Transaction data for EVM swap execution
export const swapTransactionSchema = z.object({
  to: z.string(),
  from: z.string(),
  data: z.string(),
  value: z.string(),
  chainId: z.number().int(),
});

// Transaction data for Solana swap execution
export const solanaSwapTransactionSchema = z.object({
  serialized: z.string(),
  variant: z.string(), // "versioned" or "legacy"
});

// Route hop in swap path
export const swapRouteHopSchema = z.object({
  poolAddress: z.string(),
  tokenIn: swapQuoteTokenInfoSchema,
  tokenOut: swapQuoteTokenInfoSchema,
  amountInTokens: z.string(),
  amountOutTokens: z.string(),
  exchange: z.string(),
  poolType: z.string(),
  feePercentage: z.number().optional(), // Optional for Solana swaps
});

// Route info for swap
export const swapRouteSchema = z.object({
  hops: z.array(swapRouteHopSchema),
  totalFeePercentage: z.number(),
  aggregator: z.string().optional(), // Optional for Solana swaps
});

// Complete swap quote response from API
export const swapQuoteResponseSchema = z.object({
  data: z.object({
    amountOutTokens: z.string(),
    amountInUSD: z.number(),
    amountOutUSD: z.number(),
    marketImpactPercentage: z.number(),
    tokenIn: swapQuoteTokenInfoSchema,
    tokenOut: swapQuoteTokenInfoSchema,
    requestId: z.string(),
    details: z
      .object({
        route: swapRouteSchema,
        aggregator: z.string().optional(), // Optional for Solana swaps
        raw: z.any().optional(),
      })
      .optional(),
    evm: z
      .object({
        transaction: swapTransactionSchema,
      })
      .optional(),
    solana: z
      .object({
        transaction: solanaSwapTransactionSchema,
        lastValidBlockHeight: z.number(),
      })
      .optional(),
  }),
});

// Types
export type SwapQuoteTokenInfo = z.infer<typeof swapQuoteTokenInfoSchema>;
export type SwapTransaction = z.infer<typeof swapTransactionSchema>;
export type SolanaSwapTransaction = z.infer<typeof solanaSwapTransactionSchema>;
export type SwapRouteHop = z.infer<typeof swapRouteHopSchema>;
export type SwapRoute = z.infer<typeof swapRouteSchema>;
export type SwapQuoteResponse = z.infer<typeof swapQuoteResponseSchema>;
